import { alpha } from '@mui/material/styles';
import { LOGIN_ACCENT } from '../components/auth/loginDarkTheme';
import { THEME_COLORS } from '../shared/constants/theme';

export const SHELL_MENU_ACCENT = LOGIN_ACCENT;

const shellSurfaceGradient = `linear-gradient(165deg, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`;

export function isShellMenuDark(themeMode, useMobileAppShell = false) {
  return themeMode === 'dark' || useMobileAppShell;
}

export function getMobileShellSheetPaperSx(maxHeight = 'min(78vh, 560px)') {
  return {
    borderRadius: '24px 24px 0 0',
    borderBottom: 'none',
    background: shellSurfaceGradient,
    backgroundColor: THEME_COLORS.BACKGROUND_DARK,
    border: `1px solid ${alpha('#ffffff', 0.08)}`,
    boxShadow: '0 -28px 56px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
    maxHeight,
    overflow: 'hidden',
    color: '#f8fafc',
  };
}

export function getMobileShellSheetBackdropSx() {
  return {
    backgroundColor: alpha(THEME_COLORS.BACKGROUND_DARK, 0.72),
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
  };
}

export function getMobileShellSheetHandleSx() {
  return {
    width: 44,
    height: 5,
    borderRadius: 999,
    bgcolor: alpha('#ffffff', 0.22),
    mx: 'auto',
    mt: 1.15,
    mb: 0.5,
  };
}

export function getMobileShellSheetHeaderSx(accent = SHELL_MENU_ACCENT) {
  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 1.75,
    pb: 0.35,
    color: alpha('#94a3b8', 0.95),
    '& h2': {
      margin: 0,
      color: '#f8fafc',
      fontWeight: 800,
      fontSize: '1rem',
      letterSpacing: '-0.02em',
    },
    '& .MuiSvgIcon-root': {
      color: alpha(accent, 0.65),
    },
  };
}

export function getMobileShellMenuUserCardSx(accent = SHELL_MENU_ACCENT) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 1.15,
    mx: 1.5,
    mb: 1.1,
    p: 1.25,
    borderRadius: '18px',
    background: `linear-gradient(145deg, ${alpha(accent, 0.14)} 0%, ${THEME_COLORS.SURFACE_HEADER_DARK} 100%)`,
    border: `1px solid ${alpha(accent, 0.22)}`,
    boxShadow: `inset 0 1px 0 ${alpha('#ffffff', 0.05)}`,
  };
}

export function getMobileShellMenuUserAvatarSx(accent = SHELL_MENU_ACCENT) {
  return {
    width: 52,
    height: 52,
    bgcolor: accent,
    color: '#04120e',
    fontWeight: 800,
    fontSize: '1.1rem',
    boxShadow: `0 8px 18px ${alpha(accent, 0.28)}`,
  };
}

export function getMobileShellMenuUserNameSx() {
  return {
    color: '#f8fafc',
    fontWeight: 800,
    fontSize: '0.95rem',
    lineHeight: 1.25,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}

export function getMobileShellMenuUserEmailSx() {
  return {
    color: alpha('#94a3b8', 0.98),
    fontSize: '0.76rem',
    lineHeight: 1.3,
    mt: 0.15,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };
}

export function getMobileShellMenuListSx() {
  return {
    px: 1.5,
    pb: `calc(1.35rem + env(safe-area-inset-bottom))`,
    pt: 0.15,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
  };
}

export function getMobileShellMenuItemSx(options = {}) {
  const { accent = SHELL_MENU_ACCENT, danger = false } = options;
  const tone = danger ? '#f87171' : accent;

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 1.15,
    width: '100%',
    minHeight: 58,
    px: 1.15,
    py: 1,
    borderRadius: '16px',
    cursor: 'pointer',
    textAlign: 'left',
    appearance: 'none',
    WebkitAppearance: 'none',
    border: `1px solid ${alpha('#ffffff', danger ? 0.1 : 0.08)}`,
    background: danger
      ? `linear-gradient(145deg, ${alpha('#ef4444', 0.14)} 0%, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 100%)`
      : `linear-gradient(155deg, ${alpha('#ffffff', 0.05)} 0%, ${THEME_COLORS.SURFACE_ELEVATED_DARK} 100%)`,
    color: danger ? '#fecaca' : '#f8fafc',
    transition: 'transform 0.12s ease, border-color 0.15s ease, box-shadow 0.15s ease',
    '&:active': {
      transform: 'scale(0.985)',
      borderColor: alpha(tone, 0.35),
      boxShadow: `inset 0 0 0 1px ${alpha(tone, 0.12)}`,
    },
  };
}

export function getMobileShellMenuItemIconSx(options = {}) {
  const { accent = SHELL_MENU_ACCENT, danger = false } = options;
  const tone = danger ? '#f87171' : accent;

  return {
    width: 42,
    height: 42,
    borderRadius: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: danger
      ? `linear-gradient(145deg, ${alpha('#ef4444', 0.22)} 0%, ${alpha(THEME_COLORS.SURFACE_ELEVATED_DARK, 0.95)} 100%)`
      : `linear-gradient(145deg, ${alpha(tone, 0.24)} 0%, ${alpha(THEME_COLORS.SURFACE_HEADER_DARK, 0.95)} 100%)`,
    border: `1px solid ${alpha(tone, danger ? 0.3 : 0.26)}`,
    color: tone,
  };
}

export function getMobileShellMenuItemLabelSx(danger = false) {
  return {
    color: danger ? '#fecaca' : '#f8fafc',
    fontWeight: 700,
    fontSize: '0.94rem',
    lineHeight: 1.2,
  };
}

export function getMobileShellMenuItemSubtitleSx(danger = false) {
  return {
    color: danger ? alpha('#fecaca', 0.72) : alpha('#94a3b8', 0.95),
    fontWeight: 500,
    fontSize: '0.72rem',
    lineHeight: 1.25,
    mt: 0.2,
  };
}

export function getShellMenuPaperSx(isDark) {
  return {
    background: isDark ? shellSurfaceGradient : '#ffffff',
    backgroundColor: isDark ? THEME_COLORS.BACKGROUND_DARK : '#ffffff',
    border: isDark ? `1px solid ${alpha('#ffffff', 0.08)}` : undefined,
    boxShadow: isDark
      ? '0 18px 36px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)'
      : '0 2px 15px 0 rgba(0, 0, 0, 0.15)',
    color: isDark ? '#f8fafc' : undefined,
    overflow: 'hidden',
  };
}

/** Apps / Insights top-bar grid popover — matches header chrome in dark theme. */
export function getTopBarGridMenuPaperSx(isDark) {
  const darkBg = THEME_COLORS.SURFACE_HEADER_DARK;
  const darkGradient = `linear-gradient(180deg, ${THEME_COLORS.SURFACE_HEADER_DARK} 0%, ${THEME_COLORS.BACKGROUND_DARK} 100%)`;
  const darkBorder = alpha('#ffffff', 0.08);

  return {
    mt: 1,
    borderRadius: 3,
    boxShadow: isDark
      ? '0 18px 36px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)'
      : '0 12px 32px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(76, 225, 182, 0.08)',
    border: isDark ? `1px solid ${darkBorder}` : '1px solid rgba(76, 225, 182, 0.15)',
    overflow: 'visible',
    backdropFilter: isDark ? 'none' : 'blur(20px)',
    WebkitBackdropFilter: isDark ? 'none' : 'blur(20px)',
    background: isDark ? darkGradient : 'rgba(255, 255, 255, 0.95)',
    backgroundColor: isDark ? darkBg : 'rgba(255, 255, 255, 0.95)',
    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      left: 14,
      width: 12,
      height: 12,
      bgcolor: isDark ? darkBg : 'rgba(255, 255, 255, 0.95)',
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 0,
      borderLeft: `1px solid ${isDark ? darkBorder : 'rgba(76, 225, 182, 0.15)'}`,
      borderTop: `1px solid ${isDark ? darkBorder : 'rgba(76, 225, 182, 0.15)'}`,
    },
  };
}

export function getShellMenuItemSx(isDark) {
  return {
    color: isDark ? '#f8fafc' : 'rgb(88.3561643836, 91.0068493151, 105.1438356164)',
    '&:hover': {
      backgroundColor: isDark ? alpha('#4ce1b6', 0.08) : 'rgb(228.75, 234, 249.75)',
    },
    '& .MuiListItemText-primary': {
      color: isDark ? '#f8fafc' : 'inherit',
    },
    '& .MuiListItemText-secondary': {
      color: isDark ? alpha('#94a3b8', 0.95) : '#646777',
    },
    '& .MuiListItemIcon-root': {
      color: isDark ? alpha('#94a3b8', 0.95) : '#b1c3c8',
      minWidth: 36,
    },
  };
}

export function getShellMenuUserHeaderSx(isDark) {
  return {
    px: 2,
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    borderBottom: `1px solid ${isDark ? alpha('#ffffff', 0.08) : 'rgb(223.3076923077, 227.2692307692, 235.1923076923)'}`,
  };
}
